<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';

import TextButton from '@/components/base/TextButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import DeliveryShipmentSummary from '@/components/checkout/steps/delivery/DeliveryShipmentSummary.vue';
import { useCart } from '@/composables/useCart';
import { useDelivery } from '@/composables/useDelivery';
import { isGreetingCardLineItem, Shipment } from '@/utils/cart';

defineProps<{
  isLoadingShippingOffers?: boolean;
  oneClickCheckout?: boolean;
}>();
defineEmits<{ edit: [setGiftMessage?: boolean] }>();

const store = useStore();
const { lineItems } = useCart(store);
const { hasMultipleShipments, isPickup, standardShipments } = useDelivery(store);

const hasGreetingCards = computed(() => lineItems.value.some(isGreetingCardLineItem));

const buildShippingOfferFromShipment = (shipment: Shipment) => {
  const customFields = shipment.customFields ?? {};

  return {
    earliestArrivalOn: customFields.earliestArrivalOn,
    latestArrivalOn: customFields.latestArrivalOn,
    price: customFields.price,
    name: customFields.shipmentPickupShippingOption,
    shipmentPickup: {
      carrierCode: customFields.shipmentPickupCarrierCode,
      carrierPickup: customFields.shipmentPickupCarrierPickup,
    },
  };
};
</script>

<template>
  <div>
    <div
      :class="oneClickCheckout ? 'flex flex-col mt-2.5' : 'divide-y divide-gray-200 divide-solid'"
    >
      <div
        v-for="shipment in standardShipments"
        :key="shipment.key"
        :class="{ 'py-2.5 last:pb-0': oneClickCheckout }"
      >
        <div
          v-if="hasMultipleShipments"
          class="font-semibold"
          :class="{ 'mt-3 md:mt-6': !oneClickCheckout }"
        >
          Address {{ shipment.multishipSerial }}:
          {{ shipment.address.name }}
          <template v-if="shipment.address.city">in {{ shipment.address.city }}</template>
        </div>
        <DeliveryShipmentSummary
          :class="oneClickCheckout ? 'mt-0.5' : 'mt-3'"
          :giftMessage="!!shipment.packingSlipMessage && !hasGreetingCards"
          highlight
          :hasMultipleShipments
          :isDigital="!shipment.address.street1 && !isPickup"
          :isLoading="isLoadingShippingOffers"
          :isPickup
          :oneClickCheckout
          :shippingOffer="buildShippingOfferFromShipment(shipment)"
          @edit="$emit('edit')"
        />
      </div>
    </div>
    <div
      v-if="
        oneClickCheckout &&
        !hasGreetingCards &&
        standardShipments.every((s) => !s.packingSlipMessage && s.hasPhysical) &&
        !isPickup
      "
      class="mt-5"
    >
      <BaseBodyText>Gift message option available</BaseBodyText>
      <div class="flex gap-1">
        <TextButton @click="$emit('edit', true)">
          <SmallBodyText>Add a gift message</SmallBodyText>
        </TextButton>
      </div>
    </div>
  </div>
</template>
